import { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { FiFilter, FiGrid, FiList } from 'react-icons/fi';
import axios from 'axios';
import ProductCard from '../components/product/ProductCard';
import FilterSidebar from '../components/product/FilterSidebar';
import { apiPath } from '../services/apiBase';

export default function ShopPage() {
  const { category: urlCategory } = useParams();
  const [searchParams] = useSearchParams();

  const [products, setProducts]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [total, setTotal]             = useState(0);
  const [page, setPage]               = useState(1);
  const [totalPages, setTotalPages]   = useState(1);
  const [priceRange, setPriceRange]   = useState({ min: 0, max: 5000 });
  const [showMobileFilters, setShow]  = useState(false);
  const [viewMode, setViewMode]       = useState('grid');

  const [filters, setFilters] = useState({
    category: urlCategory || '',
    ageGroup: '', gender: '', sizes: '',
    minPrice: '', maxPrice: '',
    sort: 'newest',
    q: searchParams.get('q') || '',
  });

  useEffect(() => {
    setFilters(f => ({ ...f, category: urlCategory || '', q: searchParams.get('q') || '' }));
    setPage(1);
  }, [urlCategory, searchParams]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, page, limit: 12 };
      Object.keys(params).forEach(k => { if (params[k] === '') delete params[k]; });
      const res = await axios.get(apiPath('/search'), { params });
      setProducts(res.data.products);
      setTotal(res.data.total);
      setTotalPages(res.data.pages);
      if (res.data.priceRange) setPriceRange(res.data.priceRange);
    } catch { setProducts([]); }
    finally { setLoading(false); }
  }, [filters, page]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handleFilterChange = (f) => { setFilters(f); setPage(1); };

  const activeCount = [filters.category, filters.ageGroup, filters.gender, filters.sizes, (filters.minPrice || filters.maxPrice)].filter(Boolean).length;

  const gridClass = viewMode === 'list' ? 'grid-cols-1' : 'grid-cols-2 md:grid-cols-3';

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-yamass-black capitalize">
            {filters.q ? `Results for "${filters.q}"` : filters.category ? filters.category.replace(/-/g, ' ') : 'All Products'}
          </h1>
          {!loading && <p className="text-gray-400 text-sm mt-1">{total} product{total !== 1 ? 's' : ''} found</p>}
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden md:flex items-center border border-gray-200 rounded-full overflow-hidden">
            {[['grid', FiGrid], ['list', FiList]].map(([mode, Icon]) => (
              <button key={mode} onClick={() => setViewMode(mode)} className={`p-2.5 transition-colors ${viewMode === mode ? 'bg-yamass-black text-white' : 'text-gray-400 hover:text-yamass-black'}`}>
                <Icon size={15} />
              </button>
            ))}
          </div>
          <button onClick={() => setShow(true)} className="lg:hidden flex items-center gap-2 border border-gray-200 rounded-full px-4 py-2 text-sm hover:border-yamass-accent transition-colors">
            <FiFilter size={14} /> Filters {activeCount > 0 && <span className="bg-yamass-accent text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">{activeCount}</span>}
          </button>
        </div>
      </div>

      <div className="flex gap-8">
        <aside className="hidden lg:block w-64 flex-shrink-0">
          <FilterSidebar filters={filters} onChange={handleFilterChange} priceRange={priceRange} />
        </aside>

        <div className="flex-1 min-w-0">
          {loading ? (
            <div className={`grid gap-5 ${gridClass}`}>
              {[...Array(12)].map((_, i) => (
                <div key={i}><div className="skeleton rounded-xl aspect-[3/4]" /><div className="skeleton h-3 rounded mt-3 w-3/4" /><div className="skeleton h-3 rounded mt-2 w-1/2" /></div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-24">
              <p className="text-5xl mb-4">🔍</p>
              <p className="text-gray-400 text-xl mb-2">No products found</p>
              <p className="text-gray-300 text-sm">Try adjusting your filters or search term</p>
              <button onClick={() => handleFilterChange({ category: '', ageGroup: '', gender: '', sizes: '', minPrice: '', maxPrice: '', sort: 'newest', q: '' })} className="mt-6 text-yamass-accent hover:underline text-sm font-medium">Clear all filters</button>
            </div>
          ) : (
            <div className={`grid gap-5 ${gridClass}`}>
              {products.map(p => <ProductCard key={p._id} product={p} />)}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-12">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-4 py-2 rounded-full text-sm border border-gray-200 hover:border-yamass-accent disabled:opacity-30 transition-colors">← Prev</button>
              {[...Array(Math.min(totalPages, 7))].map((_, i) => (
                <button key={i} onClick={() => setPage(i + 1)} className={`w-10 h-10 rounded-full text-sm font-medium transition-colors ${page === i + 1 ? 'bg-yamass-black text-white' : 'bg-yamass-cream text-gray-600 hover:bg-yamass-accent hover:text-white'}`}>{i + 1}</button>
              ))}
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-4 py-2 rounded-full text-sm border border-gray-200 hover:border-yamass-accent disabled:opacity-30 transition-colors">Next →</button>
            </div>
          )}
        </div>
      </div>

      {showMobileFilters && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShow(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-80 bg-white overflow-y-auto p-5 shadow-xl">
            <FilterSidebar filters={filters} onChange={handleFilterChange} priceRange={priceRange} onClose={() => setShow(false)} isMobile />
            <button onClick={() => setShow(false)} className="w-full mt-4 bg-yamass-black text-white py-3 rounded-full text-sm font-semibold">Show {total} results</button>
          </div>
        </div>
      )}
    </div>
  );
}
